import React, { useCallback, useEffect, useState } from 'react';
import { getRevenueGoal, setRevenueGoal } from '../../services/api';
import './MonthGoalWidget.css';

const money = (v) => `${Math.round(v || 0).toLocaleString('ru-RU')} ₽`;

const pluralDays = (n) => {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'дня';
  return 'дней';
};

const TargetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" />
  </svg>
);

// Цель по выручке на текущий месяц: сколько набрано, куда выводит прогноз и
// сколько нужно в день. Цифры считает сервер (digest.month_progress) — те же,
// что уходят утром в Telegram, чтобы дашборд и сводка не расходились.
const MonthGoalWidget = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getRevenueGoal());
      setError('');
    } catch (e) {
      setError(e?.response?.status === 403 ? '' : 'Не удалось загрузить цель месяца');
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft(data?.goal ? String(data.goal) : '');
    setEditing(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(String(draft).replace(/[\s ]/g, '').replace(',', '.')) || 0;
    setSaving(true);
    try {
      setData(await setRevenueGoal(data.month, amount));
      setEditing(false);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось сохранить цель');
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return null;
  if (!data) return <div className="mg-widget"><p className="ui-hint-error">{error}</p></div>;

  const p = data.progress;
  if (!p) return null;
  const goal = data.goal;
  const actualPct = goal ? Math.min((p.actual / goal) * 100, 100) : 0;
  const projectedPct = goal && p.projected ? Math.min((p.projected / goal) * 100, 100) : null;
  const reached = goal && p.actual >= goal;

  return (
    <div className="mg-widget">
      <div className="mg-header">
        <div className="mg-icon"><TargetIcon /></div>
        <div className="mg-info">
          <div className="mg-title">Цель месяца · {p.month_name}</div>
          <div className="mg-subtitle">
            Осталось {p.days_left} {pluralDays(p.days_left)} из {p.days_in_month}
          </div>
        </div>
        {data.can_edit && !editing && (
          <button type="button" className="ui-btn-ghost mg-edit" onClick={startEdit}>
            {goal ? 'Изменить' : 'Задать цель'}
          </button>
        )}
      </div>

      {editing && (
        <form className="mg-form" onSubmit={submit}>
          <label className="mg-form-label" htmlFor="mg-amount">Цель по выручке на {p.month_name.toLowerCase()}, ₽</label>
          <div className="mg-form-row">
            <input
              id="mg-amount"
              className="mg-input"
              inputMode="numeric"
              autoFocus
              placeholder="например, 3 000 000"
              value={draft}
              onChange={e => setDraft(e.target.value)}
            />
            <button type="submit" className="ui-btn-primary" disabled={saving}>
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
            <button type="button" className="ui-btn-ghost" onClick={() => setEditing(false)} disabled={saving}>
              Отмена
            </button>
          </div>
          {goal ? <p className="ui-dim mg-hint">Пустое поле или 0 — снять цель.</p> : null}
        </form>
      )}

      <div className="mg-figures">
        <div className="mg-actual">{money(p.actual)}</div>
        {goal ? <div className="mg-of">из {money(goal)}</div> : <div className="mg-of">набрано с 1-го числа</div>}
      </div>

      {goal ? (
        <>
          <div className="mg-bar" role="img"
               aria-label={`Выполнено ${Math.round((p.actual / goal) * 100)}% цели`}>
            <div className={`mg-bar-fill ${reached ? 'done' : ''}`} style={{ width: `${actualPct}%` }} />
            {projectedPct !== null && !reached && (
              <div className="mg-bar-projected" style={{ left: `${projectedPct}%` }} title="Прогноз на конец месяца" />
            )}
          </div>
          <div className="mg-rows">
            <div className="mg-row">
              <span>Выполнено</span>
              <strong>{Math.round((p.actual / goal) * 100)}%</strong>
            </div>
            {p.projected !== null && (
              <div className="mg-row">
                <span>Прогноз на конец месяца</span>
                <strong>
                  {money(p.projected)}
                  <span className={`mg-verdict ${p.on_track ? 'ok' : 'behind'}`}>
                    {p.on_track ? 'успеваем' : `${Math.round(p.projected_goal_pct)}% цели`}
                  </span>
                </strong>
              </div>
            )}
            {!reached && p.plan_today ? (
              <div className="mg-row mg-today">
                <span>План на сегодня</span>
                <strong>
                  {money(p.today_actual)} из {money(p.plan_today)}
                  <span className={`mg-verdict ${p.plan_today_pct >= 100 ? 'ok' : 'behind'}`}>
                    {Math.round(p.plan_today_pct)}%
                  </span>
                </strong>
              </div>
            ) : null}
            {!reached && p.needed_per_day ? (
              <div className="mg-row">
                <span>Нужно в день до конца месяца</span>
                <strong>{money(p.needed_per_day)}</strong>
              </div>
            ) : null}
            {reached && <div className="mg-row mg-reached">Цель месяца выполнена 🎉</div>}
          </div>
        </>
      ) : (
        <div className="mg-rows">
          {p.projected !== null && (
            <div className="mg-row">
              <span>Прогноз на конец месяца</span>
              <strong>{money(p.projected)}</strong>
            </div>
          )}
          {data.can_edit && !editing && (
            <p className="ui-dim mg-hint">
              Задайте цель — покажем темп к ней и сколько нужно зарабатывать в день.
            </p>
          )}
        </div>
      )}

      {error && <p className="ui-hint-error">{error}</p>}
    </div>
  );
};

export default MonthGoalWidget;
